/**
 * Create-on-behalf dialog (DNKADM-0061 U2; DNKADM-0349 U1 repoints the member picker at the
 * users roster) — an Owner/Admin books a reservation on behalf of anyone in the Users list,
 * against the owned, atomic v2 surface (handoff §5.15, §4.7).
 *
 * Flow:
 *  1. **Member** — the whole users roster (`listUsers`) is fetched ONCE when the dialog opens
 *     (`GET /v2/admin/users` has no server-side search or paging) and filtered CLIENT-SIDE as
 *     the operator types, debounced (DNKADM-0340). Staff rows are excluded — by the roster's
 *     own `kind` since DNKADM-0360, else by role
 *     (DNKADM-0345's rule, no toggle); a row with no linked CRM contact is shown, disabled — it
 *     can be found, but not booked, until it is linked. Picking a row selects its `contactId`.
 *  2. **Service + variation** — pick a service (`listServices`) then a variation
 *     (`listVariations`). The chosen service's assignment modes
 *     (`staffAssignment`/`resourceAssignment`, 0055) drive the staff/resource step.
 *  3. **Slot** — the availability slot picker (date range → returned slots) selects the
 *     `start` (ISO with offset). A manual ISO start field is the fallback when
 *     availability is unavailable.
 *  4. **Staff / Resource** — for a `MEMBER_SELECTS` dimension, a **REQUIRED** picker fed
 *     by the chosen slot's `staffOptions` / `resourceOptions`; `ROUND_ROBIN` / `NONE`
 *     send NO id (the server auto-assigns / omits). A `*_REQUIRED` 400 highlights the
 *     missing picker.
 *  5. **POST** `createReservation` — the full §4.2 booking error catalog maps to a clear
 *     message via `toBookingErrorMessage`.
 *
 *     **Create-on-behalf is NOT one of DNKAPI-0549's three admin override paths**
 *     (DNKADM-0457). `AdminReservationCreateRequest` carries no acknowledge field of any
 *     kind, and create runs the gated member create core: neither `StaffConflictGuard` nor
 *     `AdminOverrideGuard` is on this path. A busy coach or a full bay comes back
 *     `STAFF_UNAVAILABLE` / `RESOURCE_FULL`, mapped below; there is no "Book anyway" here,
 *     and no `acknowledgeConflict` is sent.
 *
 * **New customer (DNKADM-0413, calendar entry only).** The Member field offers two choices:
 * **Existing member** (the search above, unchanged, and the default) and **New customer** (first
 * name, last name, email, optional phone, and an Add customer button → `POST /v2/admin/customers`,
 * DNKAPI-0583). On success the returned `contactId` is selected exactly as a picked search row
 * would be, so everything below runs the one existing path. The server's `existing` flag is a
 * notice-copy HINT only (Operator rule, 2026-09-24): selection, the contactId, the identity row's
 * name and the booking path never branch on it. The member-panel entry (preset member) has no
 * choice at all. The identity row shows the STORED name the 201 returns (`firstName`/`lastName`,
 * DNKAPI-0584 / DNKADM-0414) whenever it carries one, whatever `existing` says; an older server
 * that omits them falls back to the DNKADM-0413 row's name (roster row, else what staff typed).
 *
 * The dialog owns the form; the host (calendar / member panel) supplies the active
 * `locationId` and raises `onDone` (toast + refetch) on success.
 */
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { IconAction } from '@/components/IconAction';
import { chipSx, Icon, inkAlpha, resolveTokens } from '@/theme';
import {
  createReservation,
  type CreateReservationInput,
  type Reservation,
} from '@/api/reservations';
import { CardTokenizationError, newIdempotencyKey } from '@/api/payments';
import { useOptionalOperator } from '@/auth/authHooks';
import { scrubBrand } from '@/utils/whitelabel';
import { listUsers, type UserRow } from '@/api/users';
import { addCustomer, toAddCustomerErrorView, type AddCustomerResult } from '@/api/customers';
import { getMemberCredits, type CreditBalance } from '@/api/members';
import { getService, getServiceStaff, listServices, listVariations, type V2Service } from '@/api/services';
import { listStaff, staffDisplayName } from '@/api/staff';
import { getTenantSettings } from '@/api/tenantSettings';
import { useResource } from '@/hooks/useResource';
import { dateLabel, isoClockAsIs } from '@/utils/clockTime';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useTenantCurrency } from '@/hooks/useTenantCurrency';
import type { AvailabilitySlot, SlotOption } from '@/api/availability';
import { nonEmpty, personLabel } from '@/utils/personLabel';
import { formatCreditAmount, formatMoney } from '@/utils/v2Format';
import { assignmentModeLabel, type AssignmentMode } from '@/domain/v2Enums';
import { notifyConfirmationQueueChanged } from '@/features/confirmations/confirmationQueueEvents';
import { accountStateBadge } from '@/features/members/accountState';
import { roleBadge } from '@/features/members/roleBadge';
import { AvailabilitySlotPicker } from './AvailabilitySlotPicker';
import { requiredPickerFromError, toBookingErrorMessage } from './reservationsError';
import { useReservationTender, type TenderChoice } from './reservationTender';
import { ReservationTenderFields } from './ReservationTenderFields';
import {
  outcomeIsUnknown,
  UNCONFIRMED_BOOKING_CLOSE_PROMPT,
  UNCONFIRMED_PAYMENT_CLOSE_PROMPT,
  unconfirmedMessage,
  type UnconfirmedPaymentMode,
} from '@/features/members/unconfirmedOutcome';

/**
 * DNKADM-0477 (F-216): which unknown-outcome copy an unconfirmed booking reads — a card or
 * external tender may have taken money; credits and Pay later may have made the booking.
 */
function unconfirmedMode(input: CreateReservationInput): UnconfirmedPaymentMode {
  if (input.funding === 'card') return 'card';
  if (input.funding === 'external') return 'external';
  return 'booking';
}

/** No email/booking-contact placeholder text, matching the Users table's `—`. */
const NO_EMAIL = '—';

/**
 * Render an ISO-with-offset instant's clock portion as-is (no tz shift), 12-hour
 * through the shared formatter (DNKADM-0386 U2); the raw string when it cannot be split.
 */
function clockAsIs(iso: string): string {
  return isoClockAsIs(iso) ?? iso;
}

/** "Thu, Oct 29, 2026 at 1:30 PM" — the picked slot with its day (DNKADM-0474); the clock alone if the day cannot be read. */
function selectedSlotLabel(iso: string): string {
  const day = dateLabel(iso);
  return day === null ? clockAsIs(iso) : `${day} at ${clockAsIs(iso)}`;
}

/**
 * DNKADM-0338 U2: an operator is never shown a bare identifier as a person. Every option label in
 * this dialog falls back to a neutral placeholder, never to `id`, and a whitespace-only name counts
 * as absent everywhere (`nonEmpty`, shared with the member-search dialog via `utils/personLabel`).
 */
/**
 * Debounce window (ms) for the member search-as-you-type — same as the other pickers. Since
 * DNKADM-0349 U1 the whole roster is fetched once (no per-keystroke request); the debounce now
 * governs when the client-side filter re-runs, not when a request fires.
 */
const MEMBER_SEARCH_DEBOUNCE_MS = 250;

/**
 * What the desk chose on the "How are they paying?" radio (DNKADM-0402 U1). `credits`
 * is today's default (the field is omitted from the body); the other three map to
 * dink-api's `funding` tokens verbatim.
 */
type FundingChoice = 'credits' | 'card' | 'external' | 'none';

const UNNAMED_STAFF = 'Unnamed staff';
const UNNAMED_RESOURCE = 'Unnamed resource';

function memberLabel(o: UserRow): string {
  return personLabel(o.name, o.email);
}

/**
 * The stored name a successful Add customer returned (DNKAPI-0584, DNKADM-0414): the non-blank
 * `firstName`/`lastName` joined, or `null` when the server sent neither (an older server). Read
 * from the name fields alone — never from `existing` (Operator rule, 2026-09-24). A lone first or
 * last name is still the stored name and is shown on its own, never padded with a typed part
 * (the same join as the walk-in sale's kept name). When the two returned names are EXACTLY equal
 * (after the trim `asText` applies) the name is shown once — a one-word stored name comes back as
 * `firstName="Cher"`, `lastName="Cher"` (Operator instruction, 2026-09-25). Exact match only: no
 * case folding, matching the parse, which trims and changes nothing else.
 */
function addedCustomerStoredName(result: AddCustomerResult): string | null {
  if (result.firstName !== null && result.firstName === result.lastName) return result.firstName;
  const joined = [result.firstName, result.lastName]
    .filter((part): part is string => part !== null)
    .join(' ');
  return nonEmpty(joined);
}

/** Which Member choice the calendar entry is on (DNKADM-0413). */
type MemberSource = 'existing' | 'new';

/** The New customer fields as typed. Kept across an error, a Change, and a choice switch. */
interface NewCustomerDraft {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

const EMPTY_DRAFT: NewCustomerDraft = { firstName: '', lastName: '', email: '', phone: '' };

/** A plain "something@something.tld" check. The server stays the authority on the address. */
const EMAIL_FORMAT = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type NewCustomerErrors = Partial<Record<'firstName' | 'lastName' | 'email', string>>;

function newCustomerErrors(draft: NewCustomerDraft): NewCustomerErrors {
  const errors: NewCustomerErrors = {};
  if (draft.firstName.trim() === '') errors.firstName = 'Enter a first name.';
  if (draft.lastName.trim() === '') errors.lastName = 'Enter a last name.';
  const email = draft.email.trim();
  if (email === '') errors.email = 'Enter an email.';
  else if (!EMAIL_FORMAT.test(email)) errors.email = 'Enter a valid email, like name@example.com.';
  return errors;
}

/**
 * The member a successful Add customer selects (DNKADM-0413). ONE rule, whatever the server's
 * `existing` hint says (Operator rule, 2026-09-24 — `existing` can be false for a real existing
 * customer): the roster row already loaded for this contact when there is one (its stored name
 * and email, exactly what picking it from the search would select), else a stand-in carrying the
 * typed name and email. The stand-in's other fields are never read (same as the preset stand-in).
 */
function addedCustomerRow(
  contactId: string,
  roster: readonly UserRow[],
  draft: NewCustomerDraft,
): UserRow {
  const onRoster = roster.find((u) => u.contactId === contactId);
  if (onRoster) return onRoster;
  const typedName = `${draft.firstName.trim()} ${draft.lastName.trim()}`.trim();
  return {
    userId: contactId,
    contactId,
    name: typedName === '' ? null : typedName,
    email: nonEmpty(draft.email.trim()),
    role: 'USER',
    status: 'ACTIVE',
    kind: 'CUSTOMER',
    state: null,
    membershipState: 'ACTIVE_MEMBER',
    claimed: null,
    emailVerified: null,
  };
}

/**
 * "Using 1 Bay Reservation credit." (DNKADM-0349 U2). `creditCost` renders as a plain
 * integer when whole (the common case — matches the spec's literal example) and falls
 * back to 2dp only when fractional (`formatCreditAmount`). `bucketName` is `null`
 * on a malformed/absent balance row — degrades to the generic "credit" noun rather
 * than a broken "Using 1 credit credit."
 */
function fundingSufficientMessage(creditCost: number, bucketName: string | null): string {
  return `Using ${formatCreditAmount(creditCost, bucketName)}.`;
}

/**
 * The success toast, in the desk's own terms (DNKADM-0402 U4) — it says HOW the
 * booking was paid, so nobody has to open it again to find out. `amount` is the
 * server's own figure where one resolved; `methodLabel` is the tenant payment method
 * the money came in on (already brand-scrubbed), falling back to the neutral "at the
 * desk" when the row carries no usable label.
 */
function bookedMessage(
  choice: FundingChoice,
  amount: string | null,
  methodLabel: string | null,
): string {
  if (choice === 'card') return 'Reservation booked and paid by card.';
  if (choice === 'external') {
    const how = methodLabel ?? 'paid at the desk';
    return amount === null
      ? `Reservation booked — recorded as ${how}.`
      : `Reservation booked — ${amount} recorded as ${how}.`;
  }
  if (choice === 'none') return 'Reservation booked — not paid yet.';
  return 'Reservation booked.';
}

/** The created booking's own price + tax, formatted, or null when it isn't knowable. */
function bookedAmountLabel(created: Reservation, currency: string | null): string | null {
  const code = currency ?? created.currency ?? null;
  const total = (created.priceCharged ?? 0) + (created.taxCharged ?? 0);
  return code !== null && total > 0 ? formatMoney(total, code) : null;
}

/**
 * A tender that could not be completed locally — the card field refusing to tokenize
 * (validation, or a genuine tokenization failure), or an incomplete selection. Nothing
 * has been sent at this point, so the copy says what to fix, not what failed.
 */
function tenderErrorMessage(error: unknown): string {
  if (error instanceof CardTokenizationError) {
    return error.validation
      ? 'Finish entering the card before booking.'
      : 'The card could not be read. Check the card details and try again.';
  }
  return error instanceof Error && error.message.length > 0
    ? error.message
    : 'The payment details are incomplete.';
}

interface CreateReservationDialogProps {
  open: boolean;
  /** The active location (DNKADM-0051) — the booking's REQUIRED location. */
  locationId: string;
  /** Pre-selected member (member panel), or undefined (calendar). When set, the Member field is a
   *  read-only identity row (DNKADM-0338 U3) seeded from the identity the HOST already renders —
   *  the dialog never looks the member up itself. */
  presetContactId?: string;
  presetContactName?: string | null;
  presetContactEmail?: string | null;
  onClose: () => void;
  onDone: (message: string) => void;
  /**
   * A starting point from the board (DNKADM-0389 U2 — a tap on an empty grid cell): the
   * date and wall-clock start the slot picker opens on and pre-selects when the server
   * offers that slot, and the row's bay or coach, pre-selected in its picker when the slot
   * offers it. Every field stays editable; nothing is created without Confirm.
   */
  preset?: CreateReservationPreset;
  /** The location's IANA zone (DNKADM-0390 U2: the manual start time's offset); null → device. */
  timeZone?: string | null;
}

/** The board's prefill (DNKADM-0389 U2). All optional; absent fields behave as today. */
export interface CreateReservationPreset {
  /** `YYYY-MM-DD` — the slot picker's From date. */
  date?: string;
  /** `HH:mm` wall clock on `date` — the slot to pre-select when offered. */
  startTime?: string;
  staffId?: string;
  resourceId?: string;
}

export function CreateReservationDialog({
  open,
  locationId,
  presetContactId,
  presetContactName,
  presetContactEmail,
  onClose,
  onDone,
  preset,
  timeZone = null,
}: CreateReservationDialogProps) {
  const ids = useId();
  const theme = useTheme();
  const t = resolveTokens(theme);

  // --- Member (DNKADM-0349 U1: the picker searches everyone, not just members) ------------
  const [memberQuery, setMemberQuery] = useState('');
  // DNKADM-0340 U2: the FILTER is keyed off the DEBOUNCED query (one filter pass per pause, not
  // per keystroke — the shared hook, same 250 ms as the other pickers), and starts from the
  // FIRST character. Empty / whitespace-only lists the roster (HOTFIX, below).
  const debouncedMemberQuery = useDebouncedValue(memberQuery, MEMBER_SEARCH_DEBOUNCE_MS);
  const [member, setMember] = useState<UserRow | null>(
    presetContactId
      ? {
          userId: presetContactId,
          contactId: presetContactId,
          name: presetContactName ?? null,
          email: presetContactEmail ?? null,
          // role/kind/state/status/membershipState/claimed are never read on the preset
          // (read-only identity row) path below — only `contactId`/`name`/`email` are — so
          // these are inert placeholders, mirroring the pre-0349 MemberSummary stand-in
          // fields. `kind` is `'CUSTOMER'` because `UserRow.kind` has no other value
          // (DNKADM-0367: this roster is customers only); `state` stays null since this
          // stand-in is not a real roster row.
          role: 'USER',
          status: 'ACTIVE',
          kind: 'CUSTOMER',
          state: null,
          membershipState: 'ACTIVE_MEMBER',
          claimed: true,
          emailVerified: null,
        }
      : null,
  );
  // The whole users roster, fetched ONCE when the dialog opens (it is freshly mounted per open,
  // same as `services` below) — never when `presetContactId` is set, where the picker doesn't
  // render at all. `GET /v2/admin/users` has no `search`/paging param (see `src/api/users.ts`):
  // correct to fetch-all-and-filter-client-side at 24 users (Sim Creek today), wrong at 2,000 —
  // the fix then is a `search` param on `/v2/admin/users`, not pagination bolted onto this
  // picker. Do not build that here (DNKADM-0349 stop condition).
  const usersRoster = useResource(
    () => (presetContactId === undefined ? listUsers() : Promise.resolve<UserRow[]>([])),
    [],
  );
  // The roster this picker sources (`GET /v2/admin/users`) is customers only by
  // construction (DNKADM-0367) — staff logins live under `/v2/admin/platform-members`
  // and never appear here, so there is nothing left to exclude.
  const memberCandidates = useMemo(() => usersRoster.data ?? [], [usersRoster.data]);
  // Client-side filter over the fetched roster, re-run only when the DEBOUNCED query changes —
  // this is what "one roster fetch, many filters" means in practice. Matches on name OR email
  // (not just the rendered label) so a query that hits only the email still surfaces the row.
  // HOTFIX: an empty query lists the whole roster, alphabetical by the rendered label (the
  // roster is fetched whole and is small — see above), so the picker is a list to pick from
  // on open and a filter once the operator types.
  const memberOptions = useMemo(() => {
    const query = debouncedMemberQuery.trim().toLowerCase();
    const matches =
      query === ''
        ? memberCandidates
        : memberCandidates.filter((u) =>
            `${u.name ?? ''} ${u.email ?? ''}`.toLowerCase().includes(query),
          );
    return [...matches].sort((a, b) => memberLabel(a).localeCompare(memberLabel(b)));
  }, [memberCandidates, debouncedMemberQuery]);

  // --- New customer (DNKADM-0413, calendar entry only) -------------------------
  const [memberSource, setMemberSource] = useState<MemberSource>('existing');
  const [draft, setDraft] = useState<NewCustomerDraft>(EMPTY_DRAFT);
  const [draftErrors, setDraftErrors] = useState<NewCustomerErrors>({});
  const [addError, setAddError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  // The server's answer: the stored name for the identity row (DNKADM-0414), and the notices.
  // `added.existing` is read in ONE place — the "Already a customer" notice — and nowhere else
  // (Operator rule, 2026-09-24).
  const [added, setAdded] = useState<AddCustomerResult | null>(null);

  function editDraft(field: keyof NewCustomerDraft, value: string) {
    setDraft((d) => ({ ...d, [field]: value }));
    if (field !== 'phone') setDraftErrors((e) => ({ ...e, [field]: undefined }));
  }

  function chooseMemberSource(next: MemberSource) {
    if (next === memberSource) return;
    // A different choice means nobody is selected yet; what was typed stays.
    setMemberSource(next);
    setMember(null);
    setMemberQuery('');
    setAdded(null);
    setAddError(null);
    setDraftErrors({});
  }

  /** "Change" on the added customer: back to the fields, with what staff typed. */
  function changeAddedCustomer() {
    setMember(null);
    setAdded(null);
    setAddError(null);
  }

  async function handleAddCustomer() {
    setAddError(null);
    const errors = newCustomerErrors(draft);
    setDraftErrors(errors);
    if (Object.keys(errors).length > 0) return;
    setAdding(true);
    try {
      const phone = draft.phone.trim();
      const result = await addCustomer({
        firstName: draft.firstName,
        lastName: draft.lastName,
        email: draft.email,
        ...(phone !== '' ? { phone } : {}),
      });
      // Selected by contactId alone, exactly as a picked search row — never by `existing`.
      setMember(addedCustomerRow(result.contactId, usersRoster.data ?? [], draft));
      setAdded(result);
    } catch (err) {
      setAddError(toAddCustomerErrorView(err));
    } finally {
      setAdding(false);
    }
  }

  // --- Service + variation ----------------------------------------------------
  const services = useResource(() => listServices({ status: 'active' }), []);
  const [serviceId, setServiceId] = useState('');
  const variations = useResource(
    () => (serviceId ? listVariations(serviceId) : Promise.resolve([])),
    [serviceId],
  );
  const [variationId, setVariationId] = useState('');

  // The chosen service's assignment modes (0055) — drive the staff/resource step.
  const serviceDetail = useResource(
    () => (serviceId ? getService(serviceId) : Promise.resolve<V2Service | null>(null)),
    [serviceId],
  );
  const staffMode: AssignmentMode = serviceDetail.data?.staffAssignment ?? 'NONE';
  const resourceMode: AssignmentMode = serviceDetail.data?.resourceAssignment ?? 'NONE';

  // --- Coach (DNKADM-0443 U1) -------------------------------------------------
  // Every staffed service (ROUND_ROBIN or MEMBER_SELECTS) offers a coach picker BEFORE the
  // times, default "Any coach" (no staffId sent). Options = the service's eligible staff who
  // work at this location. A pick refetches availability with `staffId` and rides on create.
  const staffed = staffMode === 'ROUND_ROBIN' || staffMode === 'MEMBER_SELECTS';
  const [coachId, setCoachId] = useState('');
  const coachPool = useResource(
    () =>
      serviceId !== '' && staffed
        ? Promise.all([getServiceStaff(serviceId), listStaff()]).then(([eligible, all]) =>
            all
              .filter(
                (m) =>
                  m.isActive &&
                  eligible.includes(m.id) &&
                  // `locations` absent = unknown (not emitted): keep; an explicit list must include this one.
                  (m.locations === undefined || m.locations.some((l) => l.id === locationId)),
              )
              .map((m) => ({ id: m.id, name: staffDisplayName(m) })),
          )
        : Promise.resolve<{ id: string; name: string }[]>([]),
    [serviceId, staffed, locationId],
  );
  const coachOptions = coachPool.data ?? [];
  // A coach that is not in the loaded pool is never sent.
  const pickedCoach = coachOptions.some((c) => c.id === coachId) ? coachId : '';

  // --- Funding (DNKADM-0349 U2, gated on DNKAPI-0503's `funding` request field) ------------
  // The service's bound bucket (1:1; `null` = a free service — nothing to draw, this whole
  // section is inert) and the selected variation's required cost.
  const creditBucketId = serviceDetail.data?.creditBucketId ?? null;
  const selectedVariation = variations.data?.find((v) => v.id === variationId) ?? null;
  // The picked person's balance for THAT bucket, fetched only once we know there is a bucket to
  // check (never for a free service, never before a variation is picked) and re-fetched only
  // when the person or the bucket changes — not on every variation click.
  const memberCredits = useResource(
    () =>
      member?.contactId && creditBucketId !== null
        ? getMemberCredits(member.contactId)
        : Promise.resolve<CreditBalance[] | null>(null),
    [member?.contactId, creditBucketId],
  );
  // The matched balance row — an absent ledger account (`data === null`) and a bucket with no
  // balance row both mean a real, confirmed 0 (not "unknown"); only "still loading" / "errored"
  // stay unknown.
  const creditBalanceRow =
    memberCredits.status === 'ready'
      ? ((memberCredits.data ?? []).find((b) => b.creditBucketId === creditBucketId) ?? null)
      : undefined; // undefined = unresolved (loading/error), distinct from `null` = confirmed 0
  /**
   * `not-applicable` — a free service, or nothing selected yet to check against: never blocks,
   * never shows a notice, never sends `funding` (server default `'credits'` applies, exactly the
   * pre-U2 behavior).
   * `unknown` — the balance read is still loading or failed: same as `not-applicable` — this is
   * the graceful-degrade case. The server remains the authority; a genuine refusal still shows
   * through DNKADM-0342's alert exactly as it did before U2 existed.
   * `sufficient` — draw credits as today (say so), send the default funding (omit the field).
   * `insufficient` — do not refuse client-side: state the check-in charge plainly and send
   * `funding: 'none'` so the server books it unfunded instead of 409ing.
   */
  const creditFundingState: 'not-applicable' | 'unknown' | 'sufficient' | 'insufficient' =
    creditBucketId === null || selectedVariation === null
      ? 'not-applicable'
      : creditBalanceRow === undefined
        ? 'unknown'
        : (creditBalanceRow?.balance ?? 0) >= selectedVariation.creditCost
          ? 'sufficient'
          : 'insufficient';
  const { currency } = useTenantCurrency();

  // --- How are they paying? (DNKADM-0402 U1/U2) -------------------------------------
  // The choice is offered once a variation is picked and the booking is not free — a
  // credit-bucket service, or one with a price above 0. It is NOT offered while the
  // balance is unresolved (`unknown`): that case keeps its pre-0402 behavior exactly —
  // no notice, no `funding` field, the server stays the authority.
  const variationPrice = selectedVariation?.price ?? 0;
  const choiceShown =
    selectedVariation !== null &&
    (creditBucketId !== null || variationPrice > 0) &&
    creditFundingState !== 'unknown';
  // "Use credits" is offered only when the balance actually covers the cost.
  const creditsOffered = creditFundingState === 'sufficient';
  const [fundingChoice, setFundingChoice] = useState<FundingChoice | null>(null);
  // The default is Use credits when it is offered, else Pay later — which keeps
  // DNKADM-0349's decision for a member with no credits (a plain notice and the normal
  // Book button, never an extra confirm). An explicit pick of a choice that stops being
  // offered (the variation changed) falls back to the same default rather than sending
  // a funding the server would refuse.
  const effectiveChoice: FundingChoice =
    fundingChoice === null || (fundingChoice === 'credits' && !creditsOffered)
      ? creditsOffered
        ? 'credits'
        : 'none'
      : fundingChoice;
  const tenderChoice: TenderChoice | null =
    choiceShown && (effectiveChoice === 'card' || effectiveChoice === 'external')
      ? effectiveChoice
      : null;
  const tender = useReservationTender(tenderChoice);
  const operator = useOptionalOperator();
  const tenantSlug = operator?.tenantSlug?.trim() ?? '';
  const priceLabel =
    currency !== null && variationPrice > 0 ? formatMoney(variationPrice, currency) : null;
  /**
   * One idempotency key per booking ATTEMPT, kept across a retry of that same attempt
   * (a network error, a decline the desk retries with the same card) so the server can
   * never make a second booking or take a second payment for it — and dropped the
   * moment any field that shapes the body changes, because that is a different booking.
   * DNKAPI-0559 REQUIRES it for card and external; DNKADM-0477 (F-216) sends it on credits
   * and Pay later too, so a re-sent booking never books or debits credits twice.
   */
  const attemptKeyRef = useRef<string | null>(null);
  /**
   * DNKADM-0475 (the DNKADM-0467 rule), on every payment mode since DNKADM-0477: a booking
   * the server never confirmed either way — a network error, timeout or 5xx after it left.
   * Its exact body (key, card nonce or method included) is kept, the form is locked, and
   * only "Check again" re-sends it; dink-api answers a key it has seen without booking,
   * debiting credits or charging twice.
   */
  const [unconfirmed, setUnconfirmed] = useState<CreateReservationInput | null>(null);

  // --- Slot -------------------------------------------------------------------
  const [slot, setSlot] = useState<AvailabilitySlot | null>(null);
  const [manualStart, setManualStart] = useState('');

  // --- Staff / resource selection (MEMBER_SELECTS) ----------------------------
  const [staffId, setStaffId] = useState('');
  const [resourceId, setResourceId] = useState('');
  // Which picker the server flagged missing (a *_REQUIRED 400) — highlighted.
  const [missingPicker, setMissingPicker] = useState<'staff' | 'resource' | null>(null);

  const [error, setError] = useState<string | null>(null);
  // DNKADM-0443 U2: the inside-notice warning copy while the confirm is open, else null.
  const [leadWarning, setLeadWarning] = useState<string | null>(null);
  // Ignores a second "Book anyway" click during the dialog's exit transition; re-armed when the
  // warning opens again.
  const leadConfirmingRef = useRef(false);
  const [submitting, setSubmitting] = useState(false);
  const locked = submitting || unconfirmed !== null;
  // DNKADM-0342: the error strip lives OUTSIDE the scrolling content (see the render) and takes
  // focus when a message lands, so a refused booking is seen — and announced — without scrolling.
  const errorRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (error) errorRef.current?.focus();
  }, [error]);

  // Reset the slot-derived selections when the service/variation/slot changes — to the
  // board's preset when the slot offers that coach/bay (DNKADM-0389 U2), else to nothing.
  const presetStaffId = preset?.staffId;
  const presetResourceId = preset?.resourceId;
  useEffect(() => {
    setStaffId(
      presetStaffId !== undefined && (slot?.staffOptions ?? []).some((o) => o.id === presetStaffId)
        ? presetStaffId
        : '',
    );
    setResourceId(
      presetResourceId !== undefined &&
        (slot?.resourceOptions ?? []).some((o) => o.id === presetResourceId)
        ? presetResourceId
        : '',
    );
    setMissingPicker(null);
  }, [serviceId, variationId, slot, presetStaffId, presetResourceId]);

  const staffOptions: SlotOption[] = slot?.staffOptions ?? [];
  const resourceOptions: SlotOption[] = slot?.resourceOptions ?? [];

  // The effective start: a picked slot, else the manual ISO fallback (trimmed).
  const effectiveStart = slot?.start ?? (manualStart.trim() || null);

  const needsStaff = staffMode === 'MEMBER_SELECTS';
  // The coach picked up front satisfies MEMBER_SELECTS; otherwise the post-slot picker's id.
  // ROUND_ROBIN with Any coach never sends an id (even when the board preset seeded `staffId`).
  const effectiveStaffId = pickedCoach !== '' ? pickedCoach : needsStaff ? staffId : '';
  const needsResource = resourceMode === 'MEMBER_SELECTS';

  // Any edit that changes the body being sent starts a NEW attempt — a retry of the
  // SAME body is the only thing that may reuse the key (DNKADM-0402 U2).
  useEffect(() => {
    attemptKeyRef.current = null;
  }, [
    member?.contactId,
    serviceId,
    variationId,
    effectiveStart,
    effectiveStaffId,
    resourceId,
    effectiveChoice,
    tender.cardOnFileId,
    tender.paymentMethodId,
  ]);

  const canSubmit =
    member?.contactId != null &&
    serviceId !== '' &&
    variationId !== '' &&
    effectiveStart !== null &&
    (!needsStaff || effectiveStaffId !== '') &&
    (!needsResource || resourceId !== '') &&
    // Book stays disabled until the chosen tender is complete (a card chosen or
    // entered, a method chosen) — U2.
    tender.ready &&
    !locked;

  function close() {
    if (submitting) return;
    // DNKADM-0475: a payment that may still land is never left silently.
    if (
      unconfirmed !== null &&
      !window.confirm(
        unconfirmedMode(unconfirmed) === 'booking'
          ? UNCONFIRMED_BOOKING_CLOSE_PROMPT
          : UNCONFIRMED_PAYMENT_CLOSE_PROMPT,
      )
    ) {
      return;
    }
    onClose();
  }

  async function handleConfirm(leadConfirmed = false) {
    setError(null);
    setMissingPicker(null);
    if (!member?.contactId || !serviceId || !variationId || !effectiveStart) {
      setError('Pick a member, service, variation, and a start time.');
      return;
    }
    if (needsStaff && !effectiveStaffId) {
      setMissingPicker('staff');
      setError('Choose a staff member for this booking.');
      return;
    }
    if (needsResource && !resourceId) {
      setMissingPicker('resource');
      setError('Choose a resource for this booking.');
      return;
    }
    // DNKADM-0443 U2: a slot inside the booking notice needs an explicit confirm before create.
    if (slot?.insideLeadTime && !leadConfirmed) {
      await openLeadConfirm();
      return;
    }
    // The tender, when one is being taken. Tokenizing the entered card happens HERE,
    // once per attempt — the nonce is single-use and never leaves this call.
    let payment: CreateReservationInput['payment'];
    if (tenderChoice !== null) {
      try {
        payment = await tender.buildPayment();
      } catch (err) {
        setError(tenderErrorMessage(err));
        return;
      }
    }
    const key = attemptKeyRef.current ?? newIdempotencyKey();
    attemptKeyRef.current = key;

    await send({
      contactId: member.contactId,
      serviceId,
      variationId,
      locationId,
      start: effectiveStart,
      // Any coach sends NO id (the server auto-assigns); a coach picked up front, or the
      // MEMBER_SELECTS post-slot pick, sends that id.
      ...(effectiveStaffId !== '' && staffed ? { staffId: effectiveStaffId } : {}),
      ...(needsResource ? { resourceId } : {}),
      // DNKADM-0402 U1 replaces DNKADM-0349 U2's derived funding with the desk's own
      // choice. `credits` — and every case where no choice is offered (a free service,
      // an unresolved balance) — sends NO `funding` field, so the server's default
      // applies exactly as it always has. Card and external carry the tender; credits and
      // pay later carry none.
      ...(choiceShown && effectiveChoice !== 'credits' ? { funding: effectiveChoice } : {}),
      ...(payment !== undefined ? { payment } : {}),
      // Every payment mode carries the attempt's key (DNKAPI-0559 for card and external,
      // DNKADM-0477 F-216 for credits and pay later).
      idempotencyKey: key,
    });
  }

  /**
   * DNKADM-0443 U2: the warning's `{n}` is the service's lead time in hours: the service
   * override, else the tenant default (read only now, when the warning is needed). If neither
   * can be read the copy drops the number rather than guess one.
   */
  async function openLeadConfirm() {
    leadConfirmingRef.current = false;
    let minutes = serviceDetail.data?.leadTimeMinutes ?? null;
    if (minutes === null) {
      try {
        minutes = (await getTenantSettings()).leadTimeMinutes;
      } catch {
        minutes = null;
      }
    }
    const name = (services.data ?? []).find((sv) => sv.id === serviceId)?.name ?? 'this service';
    if (minutes === null) {
      setLeadWarning(`This is inside the booking notice for ${name}. Book anyway?`);
      return;
    }
    const hours = minutes / 60;
    const n = Number.isInteger(hours) ? String(hours) : hours.toFixed(1);
    setLeadWarning(`This is inside the ${n}-hour booking notice for ${name}. Book anyway?`);
  }

  /** The one write path. */
  async function send(input: CreateReservationInput) {
    setError(null);
    setSubmitting(true);
    try {
      const created = await createReservation(input);
      // A requires-confirmation service books straight into the operator
      // queue — poke the shared pending-count source so the nav badge and the
      // Confirmations surfaces re-read without a manual refresh (DNKADM-0304
      // U3; mirrors the calendar's cancel-a-pending path).
      if (created.status === 'unconfirmed') notifyConfirmationQueueChanged();
      attemptKeyRef.current = null;
      setUnconfirmed(null);
      onDone(
        bookedMessage(
          choiceShown ? effectiveChoice : 'credits',
          bookedAmountLabel(created, currency) ?? priceLabel,
          scrubBrand(tender.selectedMethod?.label ?? null),
        ),
      );
    } catch (err) {
      setSubmitting(false);
      if (input.idempotencyKey !== undefined && outcomeIsUnknown(err)) {
        // DNKADM-0475/0477: the booking (its payment or credits) may have landed. Keep the
        // body under its key and lock the form until "Check again" settles it.
        setUnconfirmed(input);
        return;
      }
      setUnconfirmed(null);
      const required = requiredPickerFromError(err);
      if (required) setMissingPicker(required);
      setError(toBookingErrorMessage(err));
    }
  }

  /** DNKADM-0475: settle an unconfirmed booking by re-sending its own body and key. */
  async function checkAgain() {
    if (unconfirmed === null || submitting) return;
    await send(unconfirmed);
  }

  const titleId = `${ids}-title`;
  const fundingLabelId = `${ids}-funding`;

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth aria-labelledby={titleId}>
      <DialogTitle id={titleId} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, pb: 0.5 }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 500 }}>Book on behalf</Typography>
          <Typography variant="body2">Create a reservation for a member.</Typography>
        </Box>
        <IconAction size="small" onClick={close} label="Close">
          <Icon name="close" sx={{ fontSize: 20 }} />
        </IconAction>
      </DialogTitle>

      <DialogContent>
        <Stack sx={{ gap: 2, mt: 0.5 }}>
          {/* Member — a fixed target (member panel) is an identity row, not a locked search
              (DNKADM-0338 U3); the calendar launch keeps the search. */}
          {presetContactId !== undefined && member ? (
            <Box>
              <Typography variant="caption" sx={{ color: t.text2 }}>
                Member
              </Typography>
              <Typography sx={{ fontWeight: 500 }}>{memberLabel(member)}</Typography>
              {nonEmpty(member.email) !== null && nonEmpty(member.name) !== null && (
                <Typography variant="body2" sx={{ color: t.text2 }}>
                  {member.email}
                </Typography>
              )}
            </Box>
          ) : (
            <Stack sx={{ gap: 1.5 }}>
              {/* DNKADM-0413: Existing member (the default, the search below unchanged) or
                  New customer. Full width, so it reads as one control at phone width. */}
              <ToggleButtonGroup
                exclusive
                fullWidth
                size="small"
                value={memberSource}
                onChange={(_event, next: MemberSource | null) => {
                  if (next !== null) chooseMemberSource(next);
                }}
                aria-label="Who is this booking for"
                disabled={locked || adding}
              >
                <ToggleButton value="existing">Existing member</ToggleButton>
                <ToggleButton value="new">New customer</ToggleButton>
              </ToggleButtonGroup>
              {memberSource === 'new' ? (
                added !== null && member ? (
                  <Box>
                    <Stack
                      direction="row"
                      sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
                    >
                      <Typography variant="caption" sx={{ color: t.text2 }}>
                        Member
                      </Typography>
                      <Button size="small" onClick={changeAddedCustomer} disabled={locked}>
                        Change
                      </Button>
                    </Stack>
                    {/* DNKADM-0414: the stored name the 201 returned when it carries one, else
                        the DNKADM-0413 row's name. Never keyed on `existing`. */}
                    <Typography sx={{ fontWeight: 500 }}>
                      {addedCustomerStoredName(added) ?? memberLabel(member)}
                    </Typography>
                    {nonEmpty(member.email) !== null &&
                      (addedCustomerStoredName(added) !== null ||
                        nonEmpty(member.name) !== null) && (
                        <Typography variant="body2" sx={{ color: t.text2 }}>
                          {member.email}
                        </Typography>
                      )}
                    {/* The ONLY read of `existing` (Operator rule, 2026-09-24): notice copy. */}
                    {added.existing && (
                      <Alert severity="info" role="status" sx={{ mt: 1 }}>
                        Already a customer. Using their record.
                      </Alert>
                    )}
                    {added.invited && (
                      <Alert severity="success" role="status" sx={{ mt: 1 }}>
                        We’ve emailed them an invite to the member app.
                      </Alert>
                    )}
                  </Box>
                ) : (
                  <Stack sx={{ gap: 1.5 }}>
                    <TextField
                      label="First name"
                      required
                      size="small"
                      fullWidth
                      value={draft.firstName}
                      onChange={(e) => editDraft('firstName', e.target.value)}
                      error={draftErrors.firstName !== undefined}
                      helperText={draftErrors.firstName}
                      disabled={adding || locked}
                    />
                    <TextField
                      label="Last name"
                      required
                      size="small"
                      fullWidth
                      value={draft.lastName}
                      onChange={(e) => editDraft('lastName', e.target.value)}
                      error={draftErrors.lastName !== undefined}
                      helperText={draftErrors.lastName}
                      disabled={adding || locked}
                    />
                    <TextField
                      label="Email"
                      type="email"
                      required
                      size="small"
                      fullWidth
                      value={draft.email}
                      onChange={(e) => editDraft('email', e.target.value)}
                      error={draftErrors.email !== undefined}
                      helperText={draftErrors.email}
                      disabled={adding || locked}
                    />
                    <TextField
                      label="Phone"
                      type="tel"
                      size="small"
                      fullWidth
                      value={draft.phone}
                      onChange={(e) => editDraft('phone', e.target.value)}
                      helperText="Optional"
                      disabled={adding || locked}
                    />
                    {addError && (
                      <Alert severity="error" role="alert">
                        {addError}
                      </Alert>
                    )}
                    <Button
                      variant="outlined"
                      onClick={() => void handleAddCustomer()}
                      disabled={adding || locked}
                      startIcon={adding ? <CircularProgress size={16} color="inherit" /> : undefined}
                      sx={{ alignSelf: 'flex-start' }}
                    >
                      {adding ? 'Adding…' : 'Add customer'}
                    </Button>
                  </Stack>
                )
              ) : (
                <Autocomplete
                  options={memberOptions}
                  value={member}
                  loading={usersRoster.status === 'loading'}
                  getOptionLabel={memberLabel}
                  // memberOptions is ALREADY the fully filtered result (client-side, over the
                  // once-fetched roster) — never let MUI's own live-input label filter re-filter it
                  // a second time, which would hide a match made on a field (email) that isn't the
                  // rendered label (name).
                  filterOptions={(x) => x}
                  isOptionEqualToValue={(o, v) => o.userId === v.userId}
                  // A row with no linked CRM contact is shown (found), never selectable (not
                  // bookable) — see getOptionDisabled below.
                  getOptionDisabled={(o) => o.contactId === null}
                  onChange={(_e, value) => setMember(value && value.contactId !== null ? value : null)}
                  onInputChange={(_e, value) => setMemberQuery(value)}
                  disabled={locked}
                  renderOption={(props, option) => {
                    const { key, ...optionProps } = props;
                    const role = roleBadge(option.role, theme.palette.mode);
                    const account = accountStateBadge(option, theme.palette.mode);
                    return (
                      <Box component="li" key={key} {...optionProps}>
                        <Stack sx={{ py: 0.25, width: '100%' }}>
                          <Stack direction="row" sx={{ alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                            <Typography sx={{ fontWeight: 500 }}>{memberLabel(option)}</Typography>
                            <Chip size="small" label={role.label} sx={chipSx(role)} />
                            <Chip size="small" label={account.label} sx={chipSx(account)} />
                          </Stack>
                          <Typography variant="body2" sx={{ color: t.text2 }}>
                            {option.email ?? NO_EMAIL}
                            {option.contactId === null ? ' · No booking contact linked yet' : ''}
                          </Typography>
                        </Stack>
                      </Box>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Member"
                      required
                      size="small"
                      placeholder="Search by name or email"
                      helperText="Start typing to search."
                    />
                  )}
                />
              )}
            </Stack>
          )}

          {/* Service */}
          <TextField
            select
            label="Service"
            required
            size="small"
            fullWidth
            value={serviceId}
            onChange={(e) => {
              setServiceId(e.target.value);
              setVariationId('');
              setSlot(null);
              setCoachId('');
            }}
            disabled={locked || services.status !== 'ready'}
            helperText={services.status === 'error' ? 'Services couldn’t be loaded.' : ' '}
          >
            {(services.data ?? []).map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.name}
              </MenuItem>
            ))}
          </TextField>

          {/* Variation */}
          <TextField
            select
            label="Variation"
            required
            size="small"
            fullWidth
            value={variationId}
            onChange={(e) => {
              setVariationId(e.target.value);
              setSlot(null);
            }}
            disabled={locked || serviceId === '' || variations.status !== 'ready'}
          >
            {(variations.data ?? []).map((v) => (
              <MenuItem key={v.id} value={v.id}>
                {v.name}
                {v.isDefault ? ' (default)' : ''}
              </MenuItem>
            ))}
          </TextField>

          {/* Assignment-mode hint */}
          {serviceId !== '' && serviceDetail.status === 'ready' && (
            <Typography sx={{ fontSize: 12, color: t.text2 }}>
              Staff: {assignmentModeLabel(staffMode)} · Resource: {assignmentModeLabel(resourceMode)}
            </Typography>
          )}

          {/* How are they paying? (DNKADM-0402 U1) — the choice the desk makes, replacing
              DNKADM-0349 U2's derived notice. Silent for a free service and while the balance
              is unresolved (`unknown` never blocks or mis-labels; the server stays the
              authority and a genuine refusal still shows through the DNKADM-0342 alert
              below). Nothing here charges anyone: a card is taken only when Card is chosen
              and Book is pressed (Operator guardrail, 2026-09-22). */}
          {choiceShown && (
            <FormControl disabled={locked}>
              <FormLabel id={fundingLabelId} sx={{ fontSize: 13, fontWeight: 600 }}>
                How are they paying?
              </FormLabel>
              <RadioGroup
                aria-labelledby={fundingLabelId}
                value={effectiveChoice}
                onChange={(_event, next) => {
                  // A different tender is a different booking attempt: drop the
                  // credentials AND the key with them.
                  tender.reset();
                  setFundingChoice(next as FundingChoice);
                }}
              >
                {creditsOffered && selectedVariation && (
                  <FormControlLabel
                    value="credits"
                    control={<Radio size="small" />}
                    label={
                      <Typography sx={{ fontSize: 14 }}>
                        Use credits —{' '}
                        <Box component="span" sx={{ color: t.text2 }}>
                          {fundingSufficientMessage(
                            selectedVariation.creditCost,
                            creditBalanceRow?.bucketName ?? null,
                          )}
                        </Box>
                      </Typography>
                    }
                  />
                )}
                <FormControlLabel
                  value="card"
                  control={<Radio size="small" />}
                  label={
                    <Typography sx={{ fontSize: 14 }}>
                      Card
                      {priceLabel && (
                        <Box component="span" sx={{ color: t.text2 }}> — Charge {priceLabel} now</Box>
                      )}
                    </Typography>
                  }
                />
                <FormControlLabel
                  value="external"
                  control={<Radio size="small" />}
                  label={
                    <Typography sx={{ fontSize: 14 }}>
                      Cash or terminal
                      {priceLabel && (
                        <Box component="span" sx={{ color: t.text2 }}>
                          {' '}
                          — Record {priceLabel} paid at the desk
                        </Box>
                      )}
                    </Typography>
                  }
                />
                <FormControlLabel
                  value="none"
                  control={<Radio size="small" />}
                  label={<Typography sx={{ fontSize: 14 }}>Pay later</Typography>}
                />
              </RadioGroup>

              {/* The tender's own fields (U2) — the SAME shared controls the sell cart
                  uses, never a second copy. */}
              {tenderChoice !== null && member?.contactId && (
                <Box sx={{ mt: 1 }}>
                  <ReservationTenderFields
                    choice={tenderChoice}
                    contactId={member.contactId}
                    tenantSlug={tenantSlug}
                    locationRef={locationId}
                    tender={tender}
                    disabled={locked}
                  />
                </Box>
              )}

              {/* Pay later, said honestly (U2): nothing will be charged on its own —
                  the booking simply reads Unpaid until staff take payment. */}
              {effectiveChoice === 'none' && (
                // role="status" (not MUI's default role="alert"): an advisory notice about
                // what WILL happen on submit, not a refusal — it must never collide with
                // the DNKADM-0342 refused-booking alert's `role="alert"` query below.
                <Alert severity="warning" role="status" sx={{ mt: 1 }}>
                  Not paid yet. It will show as Unpaid until you take payment — they won’t be
                  charged automatically.
                </Alert>
              )}
            </FormControl>
          )}

          {/* Coach (DNKADM-0443 U1), before the times so a pick narrows them. */}
          {staffed && (
            <TextField
              select
              label="Coach"
              size="small"
              fullWidth
              value={pickedCoach}
              onChange={(e) => {
                setCoachId(e.target.value);
                setSlot(null);
                setManualStart('');
              }}
              disabled={locked || coachPool.status === 'loading'}
              helperText={
                coachPool.status === 'error'
                  ? 'Coaches couldn’t be loaded. Any coach will be used.'
                  : ' '
              }
              slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
            >
              <MenuItem value="">Any coach</MenuItem>
              {coachOptions.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {nonEmpty(c.name) ?? UNNAMED_STAFF}
                </MenuItem>
              ))}
            </TextField>
          )}

          {/* Slot picker — only once a service + variation are chosen */}
          {serviceId !== '' && variationId !== '' && (
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1 }}>Pick a time</Typography>
              {/* The TARGET member's availability (DNKADM-0390 U1): the admin route, keyed by
                  the picked member's contact; no member → the picker prompts and reads nothing.
                  When times fail to load the picker offers a 12-hour manual time (U2). */}
              <AvailabilitySlotPicker
                contactId={member?.contactId ?? null}
                serviceId={serviceId}
                variationId={variationId}
                locationId={locationId}
                {...(pickedCoach !== '' ? { staffId: pickedCoach } : {})}
                timeZone={timeZone}
                selectedStart={slot?.start ?? null}
                onSelect={(s) => {
                  if (locked) return;
                  setSlot(s);
                  setManualStart('');
                }}
                onManualStart={(iso) => {
                  if (locked) return;
                  setManualStart(iso ?? '');
                  setSlot(null);
                }}
                {...(preset?.date !== undefined ? { initialDate: preset.date } : {})}
                {...(preset?.startTime !== undefined ? { preferredStart: preset.startTime } : {})}
              />
              {slot && (
                <Typography sx={{ fontSize: 13, color: inkAlpha(theme, 0.7), mt: 1 }}>
                  Selected: {selectedSlotLabel(slot.start)}
                </Typography>
              )}
            </Box>
          )}

          {/* MEMBER_SELECTS staff picker — REQUIRED, fed by the slot's options */}
          {needsStaff && slot && pickedCoach === '' && (
            <TextField
              select
              label="Staff"
              required
              size="small"
              fullWidth
              value={staffId}
              onChange={(e) => setStaffId(e.target.value)}
              error={missingPicker === 'staff'}
              helperText={
                missingPicker === 'staff'
                  ? 'A staff member is required for this service.'
                  : staffOptions.length === 0
                    ? 'No staff are available for the selected slot.'
                    : ' '
              }
              disabled={locked}
            >
              {staffOptions.map((o) => (
                <MenuItem key={o.id} value={o.id}>
                  {nonEmpty(o.name) ?? UNNAMED_STAFF}
                </MenuItem>
              ))}
            </TextField>
          )}

          {/* MEMBER_SELECTS resource picker — REQUIRED, fed by the slot's options */}
          {needsResource && slot && (
            <TextField
              select
              label="Resource"
              required
              size="small"
              fullWidth
              value={resourceId}
              onChange={(e) => setResourceId(e.target.value)}
              error={missingPicker === 'resource'}
              helperText={
                missingPicker === 'resource'
                  ? 'A resource is required for this service.'
                  : resourceOptions.length === 0
                    ? 'No resources are available for the selected slot.'
                    : ' '
              }
              disabled={locked}
            >
              {resourceOptions.map((o) => (
                <MenuItem key={o.id} value={o.id}>
                  {nonEmpty(o.name) ?? UNNAMED_RESOURCE}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Stack>
      </DialogContent>

      {/* DNKADM-0342 U1: a refused booking must be visible WITHOUT scrolling. The dialog body
          above scrolls (the slot picker alone can run to many days of chips) while the actions
          below never do — so an alert at the bottom of the body sat below the fold at the moment
          of the click and read as silence. The strip is pinned here, between the scroll region
          and the actions, and takes focus when a message lands (`errorRef`). */}
      {unconfirmed !== null && (
        <Box sx={{ px: 3, pb: 2, flexShrink: 0 }}>
          <Alert
            severity="warning"
            role="alert"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => void checkAgain()}
                disabled={submitting}
              >
                Check again
              </Button>
            }
          >
            {unconfirmedMessage(unconfirmedMode(unconfirmed))}
          </Alert>
        </Box>
      )}
      {error && (
        <Box sx={{ px: 3, pb: 2, flexShrink: 0 }}>
          <Alert severity="error" role="alert" ref={errorRef} tabIndex={-1}>
            {error}
          </Alert>
        </Box>
      )}

      <DialogActions sx={{ borderTop: `1px solid ${t.lineSoft}`, px: 2.5, py: 1.5 }}>
        <Button color="inherit" onClick={close} disabled={submitting} sx={{ color: t.text2 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <Icon name="check" />}
          onClick={() => void handleConfirm(false)}
          disabled={!canSubmit}
        >
          {submitting ? 'Booking…' : 'Book reservation'}
        </Button>
      </DialogActions>

      {/* DNKADM-0443 U2: the inside-notice confirm. Cancel creates nothing. */}
      <Dialog open={leadWarning !== null} onClose={() => setLeadWarning(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Inside booking notice</DialogTitle>
        <DialogContent>
          <Typography>{leadWarning}</Typography>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setLeadWarning(null)} sx={{ color: t.text2 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (leadConfirmingRef.current) return;
              leadConfirmingRef.current = true;
              setLeadWarning(null);
              void handleConfirm(true);
            }}
          >
            Book anyway
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
